import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Restore as RestoreIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { fetchContentVersions, restoreContentVersion } from 'shared/utils/proposalApi';
import { renderMarkdownToReact } from 'shared/utils/richTextMarkdown';

const ContentVersionsDialog = ({ open, onClose, sectionId, sectionTitle, onRestore }) => {
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && sectionId) {
      loadVersions();
    }
  }, [open, sectionId]);

  const loadVersions = async () => {
    setIsLoading(true);
    const data = await fetchContentVersions(sectionId);
    setVersions(data);
    setIsLoading(false);
  };

  const handleRestore = async (versionId) => {
    const result = await restoreContentVersion(versionId);
    if (result.success) {
      onRestore && onRestore();
      onClose();
    }
  };

  const getWordCount = (text) => {
    return text ? text.split(/\s+/).filter(word => word.length > 0).length : 0;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Content Versions - {sectionTitle}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : versions.length > 0 ? (
          <>
            <List>
              {versions.map((version) => (
                <React.Fragment key={version.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">
                            Version {version.version}
                          </Typography>
                          {version.conflict_with_version && (
                            <Chip label={`Kept separately (conflicted with v${version.conflict_with_version})`} size="small" color="warning" variant="outlined" />
                          )}
                          {version.is_current && (
                            <Chip label="Current" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            By {version.author_name} • {new Date(version.created).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getWordCount(version.body)} words
                          </Typography>
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => setPreviewVersion(version)}
                        sx={{ mr: 1 }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {!version.is_current && (
                        <IconButton
                          edge="end"
                          onClick={() => handleRestore(version.id)}
                          color="primary"
                        >
                          <RestoreIcon />
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>

            {previewVersion && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Preview - Version {previewVersion.version}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ typography: 'body2' }}>
                  {renderMarkdownToReact(previewVersion.body)}
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            No version history available
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContentVersionsDialog;
